import { useCallback } from 'react';

import '@/shared/tracking/events.quiz-share-guest';
import '@/shared/tracking/events.quiz-trial-gate-copy';

import { trackEvent } from '@zep/logger/tracking';

import { useOpenWindow } from '@/hooks';
import { useUser } from '@/lib/auth';
import { setReferrer } from '@/shared/features/enter-referrer-tracking';
import {
  buildQuizCopyQuery,
  type QuizCopySource,
} from '@/shared/tracking/quizCopySource';

export interface QuizDuplicateOptions {
  /**
   * 복사 페이지까지 따라갈 유입 출처. 넘기면 URL 로 실려 인증 왕복을 넘어 살아남는다.
   *
   * `location`(= referrer)과 겹쳐 보이지만 수명이 다르다 — referrer 는 TTL 5분이라
   * 회원가입 4페이지를 넘기면 사라진다. 전환 퍼널을 이으려면 쿼리여야 한다.
   */
  copySource?: QuizCopySource;
  /** 공유 클릭 한 번을 끝까지 잇는 시도 ID. 인증 왕복을 넘어 저장까지 따라간다. */
  shareFlowId?: string;
  /** 체험 제한 화면 복사를 체험 세션에 잇는 ID. */
  trialSessionId?: string;
}

export const useQuizDuplicate = () => {
  const { user } = useUser();
  const open = useOpenWindow();

  return useCallback(
    (quizId: string, location: string, options?: QuizDuplicateOptions) => {
      setReferrer({
        from: '/play',
        location,
      });

      const duplicateUrl = `/duplicate/${quizId}${buildQuizCopyQuery({
        copySource: options?.copySource,
        shareFlowId: options?.shareFlowId,
        trialSessionId: options?.trialSessionId,
      })}`;

      if (!user) {
        // 로그인 사용자는 이 칸을 건너뛴다. copy_click 대비 비율이 곧 비로그인 비중이다.
        if (options?.copySource === 'guest_share' && options.shareFlowId) {
          trackEvent('quiz_share_guest_auth_start', {
            share_flow_id: options.shareFlowId,
            quiz_set_id: quizId,
          });
        }
        if (options?.copySource === 'trial_gate' && options.trialSessionId) {
          trackEvent('quiz_play_trial_gate_auth_start', {
            trial_session_id: options.trialSessionId,
            quiz_set_id: quizId,
          });
        }
        open(`/login?callbackUrl=${encodeURIComponent(duplicateUrl)}`);
        return;
      }

      open(duplicateUrl);
    },
    [user, open],
  );
};
